<template>
  <div class="flex flex-col bg-[#f8fafc] gap-6 overflow-hidden h-full">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Debone Inventory</h2>
      </div>

      <form class="px-6 pb-5 pt-2 shrink-0" @submit.prevent>
        <div class="flex flex-wrap items-center gap-x-12 gap-y-4">
          <div class="flex items-center gap-3 min-w-[240px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Calendar:</label>
            <input
              v-model="currentDate"
              type="date"
              @keydown.prevent
              @change="loadInventory"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
          </div>
          <div class="flex items-center gap-3 min-w-[280px]">
            <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left shrink-0">Department:</label>
            <select
              v-model="selectedDepartment"
              @change="loadInventory"
              class="flex-1 px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer"
            >
              <option value="" disabled>Select Department</option>
              <option :value="NO_DEPARTMENT">No Department</option>
              <option v-for="dept in activeDepartments" :key="dept.id" :value="String(dept.id)">{{ dept.name }}</option>
            </select>
          </div>
        </div>
      </form>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <table v-else class="w-full debone-inventory-table">
          <thead>
            <tr>
              <th rowspan="2" class="w-10 text-center sticky-col bg-[#f1f5f9] no-side-border">No.</th>
              <th rowspan="2" class="min-w-[200px] sticky-col left-[40px] bg-[#f1f5f9] no-side-border col-ingredient-name">Ingredient Name</th>
              <th rowspan="2" class="w-24 text-center">U.O.M</th>
              <th colspan="2" class="qty-group-header text-center bg-[#f1f5f9] border-b border-slate-200">Quantity</th>
              <th rowspan="2" class="w-28 text-center px-4">Unit Price</th>
              <th rowspan="2" class="w-28 text-center px-4">T.Price</th>
              <th rowspan="2" class="w-32 text-center">Remark</th>
            </tr>
            <tr>
              <th class="w-24 text-center bg-[#f1f5f9] qty-sub-header">PC</th>
              <th class="w-24 text-center bg-[#f1f5f9] qty-sub-header">KG/L</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in filteredInventory"
              :key="row.id"
              class="hover:bg-slate-50 transition-colors group"
            >
              <td class="text-center sticky-col bg-white group-hover:bg-slate-50 no-side-border">{{ index + 1 }}</td>
              <td
                class="sticky-col left-[40px] bg-white group-hover:bg-slate-50 no-side-border font-medium col-ingredient-name"
                :class="isDeboneKind(row.kind) ? 'text-green-600' : ''"
              >
                {{ row.ingredientName }}
              </td>
              <td class="col-center text-slate-600">{{ row.uomLabel }}</td>
              <td class="col-center">
                <input
                  v-model.number="row.qtyPc"
                  type="number"
                  min="0"
                  :disabled="inventoryFieldDisabled(row, 'qtyPc')"
                  :class="inventoryFieldDisabled(row, 'qtyPc') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-16 mx-auto px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center"
                  @input="calcTotalForRow(row)"
                >
              </td>
              <td class="col-center">
                <input
                  v-model.number="row.qtyKgL"
                  type="number"
                  min="0"
                  step="0.01"
                  :disabled="inventoryFieldDisabled(row, 'qtyKgL')"
                  :class="inventoryFieldDisabled(row, 'qtyKgL') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-16 mx-auto px-1 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center"
                  @input="calcTotalForRow(row)"
                >
              </td>
              <td class="col-center px-4">{{ money2(row.unitPrice) }}</td>
              <td class="font-black text-[#0d4169] col-center px-4 bg-slate-50/30">{{ money2(row.tPrice) }}</td>
              <td class="col-center">
                <input
                  v-model="row.remark"
                  type="text"
                  :disabled="inventoryFieldDisabled(row, 'remark')"
                  :class="inventoryFieldDisabled(row, 'remark') ? 'bg-slate-100 opacity-60 cursor-not-allowed' : 'bg-red-50 focus:bg-white'"
                  class="w-full max-w-[140px] mx-auto px-1.5 py-0.5 border border-transparent hover:border-[#cbd5e1] focus:border-[#0d4169] rounded outline-none transition-all text-sm text-center"
                >
              </td>
            </tr>
            <tr v-if="!loading && filteredInventory.length === 0">
              <td colspan="8" class="text-center py-10 text-slate-400 italic">No active deboni ingredients found.</td>
            </tr>
            <tr v-if="filteredInventory.length > 0" class="bg-slate-50 border-t-2 border-slate-200">
              <td colspan="6"></td>
              <td class="px-4 col-center text-slate-900 font-extrabold whitespace-nowrap">
                <span class="text-slate-700 font-bold mr-2">Total:</span>{{ money2(tPriceColumnSum) }}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div class="flex justify-center mt-6">
          <button
            type="button"
            :disabled="!canSave || loading"
            :class="(!canSave || loading) ? 'opacity-60 cursor-not-allowed' : ''"
            class="px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md hover:bg-[#0a3354] transition-all active:scale-95 shadow-md"
            @click="openSaveModal"
          >
            {{ saving ? 'Saving...' : (isSavedData ? 'Update' : 'Save') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Save confirmation modal (same pattern as dailyInventoryPage.vue) -->
  <div
    v-if="saveModal.open"
    class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
    @click.self="closeSaveModal"
  >
    <div class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all text-left relative overflow-hidden">
      <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
        <h3 class="text-base font-bold m-0">{{ isSavedData ? 'Confirm Update Debone Inventory' : 'Confirm Save Debone Inventory' }}</h3>
        <button
          type="button"
          class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
          @click="closeSaveModal"
          aria-label="Close save modal"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <div class="p-5 space-y-4">
        <div class="text-xs font-semibold text-slate-500">Please enter the code below to confirm that you want to save.</div>
        <div class="px-3 py-2 rounded-md border border-slate-200 bg-green-100 text-[#0d4169] text-sm font-bold tracking-[0.25em] text-center">
          {{ saveModal.code }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[0.78rem] font-semibold text-[#1e293b]">Enter Code</label>
          <input
            v-model.trim="saveModal.input"
            type="text"
            maxlength="4"
            class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm uppercase"
          >
        </div>
        <div v-if="saveModal.error" class="text-xs text-red-600 font-semibold">{{ saveModal.error }}</div>
      </div>

      <div class="px-5 py-4 flex items-center justify-end gap-2">
        <button type="button" @click="closeSaveModal" class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors">Cancel</button>
        <button
          type="button"
          @click="confirmSave"
          :disabled="saving"
          class="px-5 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all disabled:opacity-60"
        >
          {{ saving ? 'Saving...' : 'Confirm' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const NO_DEPARTMENT = 'none'

const currentDate = ref('')
const selectedDepartment = ref('')
const todayDate = ref(new Date().toISOString().split('T')[0])
const departments = ref([])
const registrations = ref([])
const inventoryItems = ref([])
const loading = ref(false)
const saving = ref(false)
const isSnapshotLocked = ref(false)
const isSavedData = ref(false)

const saveModal = ref({
  open: false,
  code: '',
  input: '',
  error: '',
})

const LEGACY_UOM_TO_LABEL = { Kg: 'K.G.', Ltr: 'liter', Pc: 'Pieces' }

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

const activeDepartments = computed(() =>
  departments.value.filter((d) => (d.status ?? 1) === 1).sort((a, b) => a.name.localeCompare(b.name))
)

function isActiveDeboneRegistration(reg) {
  return (reg?.status ?? 1) === 1
}

const activeRegistrations = computed(() =>
  registrations.value.filter(isActiveDeboneRegistration)
)

function getRegistrationDepartmentId(reg) {
  const raw = reg.departmentId ?? reg.department_id ?? null
  if (raw === undefined || raw === null || raw === '') return null
  return String(raw)
}

const departmentFilteredRegistrations = computed(() => {
  if (!selectedDepartment.value) return []
  if (selectedDepartment.value === NO_DEPARTMENT) {
    return activeRegistrations.value.filter((r) => getRegistrationDepartmentId(r) === null)
  }
  return activeRegistrations.value.filter(
    (r) => getRegistrationDepartmentId(r) === String(selectedDepartment.value)
  )
})

function normalizeUnit(unit) {
  return (unit || '').toLowerCase().replace(/[^a-z0-9]/g, '')
}

function isLiterOrKgUnit(unit) {
  const unitNorm = normalizeUnit(unit)
  return (
    unitNorm === 'l' ||
    unitNorm === 'liter' ||
    unitNorm === 'liters' ||
    unitNorm.startsWith('liter') ||
    unitNorm === 'kg' ||
    unitNorm === 'kilogram'
  )
}

function isPiecesUomValue(uom) {
  const unitNorm = normalizeUnit(uom)
  return unitNorm === 'pieces' || unitNorm === 'pc' || uom === 'Pieces' || uom === 'Pc'
}

/** Same derivation as itemPage.vue getDerivedItemRecentPrice */
function getDerivedItemRecentPrice(unit, recentUnitPrice, recentVolume) {
  const price = Number(recentUnitPrice || 0)
  if (!isLiterOrKgUnit(unit)) return price

  const volume = Number(recentVolume || 0)
  if (!(volume > 0)) return 0

  return (price / volume) * 1000
}

function getUomLabel(uom) {
  if (!uom) return '-'
  if (uom === 'K.G.' || uom === 'liter' || uom === 'Pieces') return uom
  return LEGACY_UOM_TO_LABEL[uom] || uom
}

/**
 * Deboni / green Ingredient Name Unit Price — same as deboniPage.vue Debone Percentage table.
 * registrations API already derives unitPrice from products.recent_unit_price / recent_volume
 * (see getDeboneProductUnitPrice in deboneRegistration.controller.js). Do not divide by quantity.
 */
function getProductUnitPrice(reg) {
  return Number(reg?.unitPrice ?? reg?.unit_price ?? 0)
}

/** Resolve current registration Unit Price for a saved/green debone inventory row. */
function resolveDeboneRowUnitPrice(row) {
  const regId = Number(row?.deboneRegistrationId ?? row?.debone_registration_id)
  if (Number.isFinite(regId) && regId > 0) {
    const byId = activeRegistrations.value.find((r) => Number(r.id) === regId)
    if (byId) return getProductUnitPrice(byId)
  }
  const name = String(row?.ingredientName || '').trim().toLowerCase()
  if (name) {
    const byName = activeRegistrations.value.find(
      (r) => String(r.productName || r.deboniName || '').trim().toLowerCase() === name
    )
    if (byName) return getProductUnitPrice(byName)
  }
  return Number(row?.unitPrice ?? 0)
}

function isDeboneKind(kind) {
  const k = String(kind || '').toLowerCase()
  return k === 'debone' || k === 'product'
}

function mergeIngredientEntry(existing, entry) {
  if (!existing) return { ...entry }
  if (entry.kind === 'item' && isDeboneKind(existing.kind)) {
    return { ...entry }
  }
  if (existing.kind === 'item') {
    return { ...existing }
  }
  return {
    ...existing,
    uom: existing.uom || entry.uom,
    unitPrice: existing.unitPrice ?? entry.unitPrice,
  }
}

function resolveItemForRegistration(reg, itemsById) {
  const byId = itemsById.get(Number(reg.itemId))
  if (byId) return byId

  const itemName = String(reg.itemName || '').trim().toLowerCase()
  if (!itemName) return null

  return (
    inventoryItems.value.find(
      (i) => String(i.itemName || '').trim().toLowerCase() === itemName
    ) || null
  )
}

/**
 * Build a unique ingredient list from every active debone_registration row:
 * - all Deboni Item Names (parent items)
 * - all Deboni Product Names (deboni names)
 * Names are de-duplicated case-insensitively; item rows take precedence on collision.
 */
function buildUniqueIngredientList(activeRegs, itemsById) {
  const byName = new Map()

  for (const reg of activeRegs) {
    const item = resolveItemForRegistration(reg, itemsById)
    const itemName = String(reg.itemName || item?.itemName || '').trim()
    const productName = String(reg.productName || reg.deboniName || '').trim()

    if (itemName) {
      const itemKey = itemName.toLowerCase()
      const itemEntry = {
        name: itemName,
        uom: item?.unit || '',
        kind: 'item',
        itemId: reg.itemId ?? item?.id ?? null,
        unitPrice: item
          ? getDerivedItemRecentPrice(item.unit, item.recent_unit_price, item.recent_volume)
          : 0,
      }
      byName.set(itemKey, mergeIngredientEntry(byName.get(itemKey), itemEntry))
    }

    if (productName) {
      const productKey = productName.toLowerCase()
      const productEntry = {
        name: productName,
        uom: reg.uom || '',
        kind: 'debone',
        registrationId: reg.id ?? null,
        unitPrice: getProductUnitPrice(reg),
      }
      byName.set(productKey, mergeIngredientEntry(byName.get(productKey), productEntry))
    }
  }

  return Array.from(byName.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  )
}

function createInventoryRow(entry, preset = {}) {
  const usesPc = isPiecesUomValue(entry.uom)
  const usesKgL = isLiterOrKgUnit(entry.uom) || entry.uom === 'Kg' || entry.uom === 'Ltr'
  const row = {
    id: `${entry.kind}:${entry.name}`,
    deboneInventoryId: preset.deboneInventoryId ?? null,
    ingredientName: entry.name,
    kind: entry.kind,
    type: entry.kind,
    itemId: entry.itemId ?? null,
    deboneRegistrationId: entry.registrationId ?? null,
    uom: entry.uom || null,
    uomLabel: getUomLabel(entry.uom),
    usesPc,
    usesKgL,
    qtyPc: preset.qtyPc ?? (entry.kind === 'item' || usesPc ? '' : ''),
    qtyKgL: preset.qtyKgL ?? (entry.kind === 'item' || usesKgL ? '' : ''),
    unitPrice: preset.unitPrice ?? entry.unitPrice ?? 0,
    tPrice: preset.tPrice ?? 0,
    remark: preset.remark ?? '',
  }
  calcTotalForRow(row)
  return row
}

function mapSavedRowToInventory(row) {
  const raw = String(row.type || row.kind || 'item').toLowerCase()
  const kind = raw === 'debone' || raw === 'product' ? 'debone' : 'item'
  const uom = row.uom || ''
  const usesPc = isPiecesUomValue(uom)
  const usesKgL = isLiterOrKgUnit(uom) || uom === 'Kg' || uom === 'Ltr'
  // Green/debone rows: Unit Price from products via registrations (same as deboniPage).
  // Item rows: keep stored unit price as before.
  const unitPrice = kind === 'debone'
    ? resolveDeboneRowUnitPrice(row)
    : Number(row.unitPrice ?? 0)
  const mapped = {
    id: `${kind}:${row.ingredientName}`,
    deboneInventoryId: row.id,
    ingredientName: row.ingredientName,
    kind,
    type: kind,
    itemId: row.itemId ?? null,
    deboneRegistrationId: row.deboneRegistrationId ?? null,
    uom,
    uomLabel: getUomLabel(uom),
    usesPc,
    usesKgL,
    qtyPc: row.qtyPc ?? '',
    qtyKgL: row.qtyKgL ?? '',
    unitPrice,
    tPrice: Number(row.tPrice ?? row.totalPrice ?? 0),
    remark: row.remark ?? '',
  }
  calcTotalForRow(mapped)
  return mapped
}

const inventory = ref([])

function buildItemsById() {
  const map = new Map()
  for (const item of inventoryItems.value) {
    map.set(Number(item.id), item)
  }
  return map
}

function buildBaseInventoryRows() {
  const uniqueEntries = buildUniqueIngredientList(departmentFilteredRegistrations.value, buildItemsById())
  return uniqueEntries.map((entry) => createInventoryRow(entry))
}

function initInventory() {
  inventory.value = sortByIngredientName(buildBaseInventoryRows())
}

const filteredInventory = computed(() => inventory.value)

function inventoryDepartmentQueryValue() {
  if (selectedDepartment.value === NO_DEPARTMENT) return NO_DEPARTMENT
  return selectedDepartment.value
}

function sortByIngredientName(rows) {
  const arr = Array.isArray(rows) ? rows : []
  return [...arr].sort((a, b) =>
    String(a?.ingredientName || '').localeCompare(
      String(b?.ingredientName || ''),
      undefined,
      { sensitivity: 'base' }
    )
  )
}

function buildEmptySnapshotRow(row) {
  const empty = {
    ...row,
    qtyPc: 0,
    qtyKgL: 0,
    tPrice: 0,
    remark: '',
  }
  calcTotalForRow(empty)
  return empty
}

async function parseSavedInventoryResponse(savedRes) {
  if (!savedRes?.ok) {
    return { savedRows: [], pendingEditAccess: [], hasData: false }
  }
  const savedPayload = await savedRes.json().catch(() => ({ hasData: false, rows: [] }))
  let savedRows = []
  if (Array.isArray(savedPayload)) {
    savedRows = savedPayload
  } else if (Array.isArray(savedPayload?.rows)) {
    savedRows = savedPayload.rows
  }
  return {
    savedRows,
    pendingEditAccess: Array.isArray(savedPayload?.pendingEditAccess) ? savedPayload.pendingEditAccess : [],
    hasData: Boolean(savedPayload?.hasData) || savedRows.length > 0,
  }
}

function applyPendingEditPermissions(rows, pendingList) {
  const list = Array.isArray(pendingList) ? pendingList : []
  const flagOn = (p, key) => {
    const v = p[key]
    return v === true || v === 1
  }
  return rows.map((row) => {
    const rid = row.deboneInventoryId
    const relevant = list.filter(
      (p) => rid != null && Number.isFinite(Number(rid)) && Number(p.deboneInventoryId) === Number(rid)
    )
    return {
      ...row,
      canEditQtyPc: relevant.some((p) => flagOn(p, 'qtyPc')),
      canEditQtyKgL: relevant.some((p) => flagOn(p, 'qtyKgL')),
      canEditRemark: relevant.some((p) => flagOn(p, 'remark')),
    }
  })
}

function isFutureDateSelected() {
  if (!currentDate.value) return false
  return currentDate.value > todayDate.value
}

function isPastDateSelected() {
  if (!currentDate.value) return false
  return currentDate.value < todayDate.value
}

function inventoryFieldDisabled(row, field) {
  if (isFutureDateSelected()) return true
  if (field === 'qtyPc' && !isQtyPcEnabled(row)) return true
  if (field === 'qtyKgL' && !isQtyKgLEnabled(row)) return true
  if (isSnapshotLocked.value) {
    if (field === 'qtyPc') return !row.canEditQtyPc
    if (field === 'qtyKgL') return !row.canEditQtyKgL
    if (field === 'remark') return !row.canEditRemark
    return true
  }
  return false
}

function hasMeaningfulRowData(row) {
  const qtyPc = Number(row?.qtyPc || 0)
  const qtyKgL = Number(row?.qtyKgL || 0)
  const remark = String(row?.remark ?? '').trim()
  return qtyPc !== 0 || qtyKgL !== 0 || remark.length > 0
}

async function loadEditableBaseInventory() {
  isSnapshotLocked.value = false
  isSavedData.value = false
  initInventory()
}

async function loadInventory() {
  if (!selectedDepartment.value || !currentDate.value) {
    inventory.value = []
    isSnapshotLocked.value = false
    isSavedData.value = false
    return
  }

  loading.value = true
  try {
    // Future dates: show department-filtered base list only, never saved snapshots,
    // always read-only with zero values (same as dailyInventoryPage.vue).
    if (isFutureDateSelected()) {
      isSnapshotLocked.value = true
      isSavedData.value = false
      inventory.value = sortByIngredientName(
        buildBaseInventoryRows().map(buildEmptySnapshotRow)
      )
      return
    }

    // If rows already saved for this department + calendar, load from debone_inventory and lock editing.
    const savedRes = await apiFetch(
      `/debones/inventory?departmentId=${encodeURIComponent(inventoryDepartmentQueryValue())}&calendar=${currentDate.value}`
    )
    const { savedRows, pendingEditAccess } = await parseSavedInventoryResponse(savedRes)

    if (savedRows.length > 0) {
      isSnapshotLocked.value = true
      isSavedData.value = true
      const processedRows = sortByIngredientName(savedRows.map(mapSavedRowToInventory))
      inventory.value = applyPendingEditPermissions(processedRows, pendingEditAccess)
      return
    }

    // No saved snapshot for a past date: show editable base list (same as dailyInventoryPage.vue).
    if (isPastDateSelected()) {
      await loadEditableBaseInventory()
      return
    }

    // No saved snapshot for today: show editable base list.
    await loadEditableBaseInventory()
  } catch (err) {
    console.error('Error loading debone inventory:', err)
    await loadEditableBaseInventory()
  } finally {
    loading.value = false
  }
}

async function loadDepartments() {
  if (!companyId.value) return
  let endpoint = `/departments/company/${companyId.value}`
  if (branchId.value) {
    endpoint = `/departments/branch/${branchId.value}`
  }
  const response = await apiFetch(endpoint, { method: 'GET' })
  if (!response.ok) return
  const data = await response.json().catch(() => [])
  departments.value = Array.isArray(data) ? data : []
}

async function loadItems() {
  const params = new URLSearchParams()
  if (branchId.value) params.append('branchId', branchId.value)
  else if (companyId.value) params.append('companyId', companyId.value)
  else return

  const response = await apiFetch(`/items?${params.toString()}`)
  if (!response.ok) return
  const data = await response.json().catch(() => [])
  inventoryItems.value = Array.isArray(data) ? data : []
}

async function loadRegistrations() {
  const response = await apiFetch('/debones/registrations', { method: 'GET' })
  if (!response.ok) {
    registrations.value = []
    return
  }
  const data = await response.json().catch(() => [])
  registrations.value = (Array.isArray(data) ? data : []).filter(isActiveDeboneRegistration)
}

async function loadAll() {
  loading.value = true
  try {
    await Promise.all([loadDepartments(), loadItems(), loadRegistrations()])
    if (selectedDepartment.value) {
      await loadInventory()
    }
  } finally {
    loading.value = false
  }
}

function generateVerificationCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let out = ''
  for (let i = 0; i < 4; i++) {
    out += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return out
}

function openSaveModal() {
  if (!canSave.value) return
  saveModal.value.open = true
  saveModal.value.code = generateVerificationCode()
  saveModal.value.input = ''
  saveModal.value.error = ''
}

function closeSaveModal() {
  if (saving.value) return
  saveModal.value.open = false
}

async function confirmSave() {
  const expected = String(saveModal.value.code || '').trim()
  const entered = String(saveModal.value.input || '').trim().toUpperCase()
  if (!entered || entered !== expected) {
    saveModal.value.error = 'Invalid code. Please enter the exact 4-character code.'
    return
  }
  saveModal.value.error = ''
  const ok = await saveAllRows()
  if (ok) {
    saveModal.value.open = false
  }
}

const tPriceColumnSum = computed(() =>
  filteredInventory.value.reduce((sum, row) => sum + Number(row?.tPrice || 0), 0)
)

const canSave = computed(() => {
  if (saving.value) return false
  if (!currentDate.value || !selectedDepartment.value) return false
  if (isFutureDateSelected()) return false
  if (filteredInventory.value.length === 0) return false
  if (isSnapshotLocked.value) {
    return filteredInventory.value.some(
      (row) => row.canEditQtyPc || row.canEditQtyKgL || row.canEditRemark
    )
  }
  return filteredInventory.value.some(hasMeaningfulRowData)
})

function isQtyPcEnabled(row) {
  return row.kind === 'item' || row.usesPc
}

function isQtyKgLEnabled(row) {
  return row.kind === 'item' || row.usesKgL
}

function getActiveQty(row) {
  if (row.kind === 'item') {
    return (Number(row.qtyPc) || 0) + (Number(row.qtyKgL) || 0)
  }
  if (row.usesPc) return Number(row.qtyPc) || 0
  if (row.usesKgL) return Number(row.qtyKgL) || 0
  return 0
}

function calcTotalForRow(row) {
  row.tPrice = getActiveQty(row) * Number(row.unitPrice || 0)
}

function money2(val) {
  return Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

async function saveAllRows() {
  if (!canSave.value) return false
  saving.value = true
  try {
    const itemsToSave = (Array.isArray(inventory.value) ? inventory.value : []).map((row) => {
      const rawKind = row.kind || row.type || 'item'
      const normalizedKind = isDeboneKind(rawKind) ? 'debone' : 'item'
      return {
        ...row,
        deboneInventoryId: row.deboneInventoryId ?? null,
        type: normalizedKind,
        kind: normalizedKind,
        itemId: row.itemId ?? null,
        deboneRegistrationId: row.deboneRegistrationId ?? null,
        ingredientName: row.ingredientName,
        uom: row.uom,
        qtyPc: Number(row?.qtyPc || 0),
        qtyKgL: Number(row?.qtyKgL || 0),
        unitPrice: Number(row?.unitPrice || 0),
        tPrice: Number(row?.tPrice || 0),
        remark: row?.remark ?? '',
      }
    })

    if (itemsToSave.length === 0) {
      alert('No inventory rows to save.')
      return false
    }

    const res = await apiFetch('/debones/inventory/batch', {
      method: 'POST',
      body: JSON.stringify({
        date: currentDate.value,
        calendar: currentDate.value,
        departmentId: inventoryDepartmentQueryValue(),
        items: itemsToSave,
      }),
    })

    if (res.ok) {
      alert(isSavedData.value ? 'All rows updated successfully!' : 'All rows saved successfully!')
      await loadInventory()
      return true
    }

    const err = await res.json().catch(() => ({}))
    alert('Error saving: ' + (err.message || 'Unknown error'))
    return false
  } catch (err) {
    console.error('Error saving debone inventory:', err)
    alert('Failed to save rows')
    return false
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  await loadAll()
})
</script>

<style scoped>
.debone-inventory-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th,
td {
  padding: 8px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.debone-inventory-table thead th {
  text-align: center;
}

.debone-inventory-table thead th.qty-group-header {
  text-align: center;
}

.debone-inventory-table thead th.col-ingredient-name,
.debone-inventory-table td.col-ingredient-name {
  text-align: left;
}

.debone-inventory-table td.col-center {
  text-align: center;
}

thead tr:nth-child(2) .qty-sub-header {
  top: 33px;
  z-index: 19;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
  border-right: 2px solid #e2e8f0 !important;
}

.no-side-border {
  border-right: none !important;
  border-left: none !important;
}

th.sticky-col {
  z-index: 30;
}

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
